// src/auth/AccountMenu.jsx
// Navbar account element: "Sign in" pill when signed out, email chip with a
// dropdown (subscription line + billing / sign-out actions) when signed in.
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useSubscription } from "./useSubscription";
import "./AccountMenu.css";

function AccountMenu() {
  const { user, loading: authLoading, openLogin, signOut } = useAuth();
  const { profile, status, isAdmin, isSubscriber, startCheckout, openPortal } =
    useSubscription();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  // Click-outside to close the dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [open]);

  if (authLoading) return null;

  if (!user) {
    return (
      <button type="button" className="am-signin" onClick={openLogin}>
        Sign in
      </button>
    );
  }

  const email = user.email || "";
  const localPart = email.split("@")[0] || "Account";

  let badge = null;
  if (isAdmin) {
    badge = "Admin";
  } else if (isSubscriber) {
    badge = status === "trialing" ? "Trial" : "Pro";
  }

  let subscriptionLine = "Free account";
  if (isAdmin) {
    subscriptionLine = "Admin — full access";
  } else if (status === "trialing") {
    subscriptionLine =
      profile && profile.trial_ends_at
        ? `Free trial until ${new Date(profile.trial_ends_at).toLocaleDateString()}`
        : "Free trial";
  } else if (isSubscriber) {
    subscriptionLine = "Subscribed";
  }

  const handleSubscribe = async () => {
    setBusy(true);
    await startCheckout();
    setBusy(false);
  };

  const handlePortal = async () => {
    setBusy(true);
    await openPortal();
    setBusy(false);
  };

  const handleSignOut = async () => {
    setBusy(true);
    await signOut();
    setBusy(false);
    setOpen(false);
  };

  return (
    <div className="am-root" ref={rootRef}>
      <button
        type="button"
        className="am-chip"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="am-chip-name">{localPart}</span>
        {badge && <span className="am-badge">{badge}</span>}
        <svg
          className={`am-caret ${open ? "am-caret-open" : ""}`}
          viewBox="0 0 24 24"
          width="14"
          height="14"
          aria-hidden="true"
        >
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="am-dropdown" role="menu">
          <div className="am-info">
            <div className="am-email">{email}</div>
            <div className="am-subline">{subscriptionLine}</div>
          </div>
          <div className="am-divider" />
          {!isSubscriber && (
            <button
              type="button"
              className="am-item am-item-primary"
              onClick={handleSubscribe}
              disabled={busy}
            >
              Subscribe
            </button>
          )}
          {profile && profile.stripe_customer_id && (
            <button
              type="button"
              className="am-item"
              onClick={handlePortal}
              disabled={busy}
            >
              Manage billing
            </button>
          )}
          <button
            type="button"
            className="am-item am-item-muted"
            onClick={handleSignOut}
            disabled={busy}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default AccountMenu;
